import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import BaseDialog from "../utils/dialog/BaseDialog";
import RolloverYearSummary from "./RolloverYearSummary";

const RolloverProgressDialog = ({
  status,
  error,
  sourceYear,
  targetYear,
  onClose,
}) => {
  const [takingLonger, setTakingLonger] = useState(false);
  const processing = status === "processing";
  const complete = status === "complete";

  useEffect(() => {
    setTakingLonger(false);
    if (!processing) return;
    const timer = setTimeout(() => setTakingLonger(true), 15000);
    return () => clearTimeout(timer);
  }, [processing]);

  return (
    <BaseDialog
      open={status !== "idle"}
      onClose={onClose}
      closeDisabled={processing || complete}
      maxWidth='md'
      paperSx={{ maxWidth: 780 }}
      contentSx={{ px: { xs: 2, sm: 3 } }}
      title={
        processing
          ? "Applying rollover"
          : complete
            ? "Rollover completed"
            : "Check rollover status"
      }
      subtitle={
        processing
          ? "Carrying forward the selected balances and schedules."
          : complete
            ? "Your carried-forward data is ready to review."
            : "Review the current computation before trying again."
      }
      icon={
        complete ? (
          <CheckCircleOutlineIcon />
        ) : processing ? (
          <RefreshIcon />
        ) : (
          <ErrorOutlineIcon />
        )
      }
      iconColor={
        complete ? "success.main" : processing ? "primary.main" : "error.main"
      }
      actions={
        !processing && (
          <>
            {!complete && (
              <Button
                onClick={onClose}
                color='inherit'
                sx={{
                  mr: "auto",
                  color: "text.secondary",
                  textTransform: "none",
                }}
              >
                Close
              </Button>
            )}
            <Button
              variant='contained'
              onClick={() => window.location.reload()}
              sx={{ textTransform: "none" }}
            >
              Refresh {complete ? "now" : "and check"}
            </Button>
          </>
        )
      }
    >
      <Stack
        spacing={2.5}
        role='status'
        aria-live='polite'
        aria-busy={processing}
      >
        <RolloverYearSummary
          sourceYear={sourceYear}
          targetYear={targetYear}
          animate={processing}
        />
        <Paper variant='outlined' sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
          {processing ? (
            <Stack
              alignItems='center'
              spacing={2}
              sx={{ py: 2, textAlign: "center" }}
            >
              <CircularProgress
                size={40}
                thickness={3.5}
                aria-label='Applying rollover'
              />
              <Box>
                <Typography
                  variant='subtitle1'
                  fontWeight={600}
                  sx={{ mb: 0.5 }}
                >
                  Applying your selected data
                </Typography>
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ lineHeight: 1.7 }}
                >
                  Keep this window open until the request finishes.
                </Typography>
              </Box>
            </Stack>
          ) : complete ? (
            <Stack direction='row' spacing={1.5} alignItems='flex-start'>
              <CheckCircleOutlineIcon
                sx={{ color: "success.main", mt: 0.25 }}
              />
              <Box>
                <Typography
                  variant='subtitle1'
                  fontWeight={600}
                  sx={{ mb: 0.5 }}
                >
                  Your rollover has been saved.
                </Typography>
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ lineHeight: 1.7 }}
                >
                  Refreshing the computation. Review the rolled-over data after
                  the page reloads.
                </Typography>
              </Box>
            </Stack>
          ) : (
            <Stack spacing={1.5}>
              <Typography
                variant='subtitle2'
                color='error.main'
                fontWeight={600}
                role='alert'
                sx={{ overflowWrap: "anywhere" }}
              >
                {error}
              </Typography>
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ lineHeight: 1.7 }}
              >
                Refresh and check the current computation before starting
                another rollover. A connection error may occur after the server
                has completed the request.
              </Typography>
            </Stack>
          )}
        </Paper>
        {processing && takingLonger && (
          <Stack direction='row' spacing={1} alignItems='flex-start'>
            <InfoOutlinedIcon
              sx={{ color: "text.secondary", fontSize: 18, mt: 0.25 }}
            />
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{ lineHeight: 1.7 }}
            >
              This is taking longer than usual. The request is still running;
              you do not need to submit it again.
            </Typography>
          </Stack>
        )}
      </Stack>
    </BaseDialog>
  );
};

export default RolloverProgressDialog;
